import React, { useState } from 'react';
import { Icon } from '@rv-life/design-system';
import { MFE_ENVIRONMENTS } from '@/constants';
import { getOriginFromUrl } from '@/utils/url';
import css from './VersionOverridePanel.module.css';
import { useDevTools } from '../../DevToolsProvider';
import { MfeVersionsType } from '../../hooks/useMfeVersions';
import { resetDevToolsStorage, saveDevToolsStorage } from '../../utils';

interface VersionOverridePanelProps {
	onBack: () => void;
	onClose: () => void;
}

const sanitizeInput = (input: string): string => {
	return input.replace(/[<>'&]/g, '').slice(0, 20);
};

interface VersionInputRowProps {
	label: string;
	baseUrl: string;
	value: string;
	onChange: (value: string) => void;
}

const VersionInputRow = ({ label, baseUrl, value, onChange }: VersionInputRowProps) => {
	const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const sanitizedValue = sanitizeInput(e.target.value);
		onChange(sanitizedValue);
	};

	const inputId = `version-input-${label.toLowerCase()}`;
	const displayUrl = value ? `${baseUrl}/${value}` : baseUrl;

	return (
		<div className={css.inputRow}>
			<div className={css.inputLabelGroup}>
				<label className={css.inputLabel} htmlFor={inputId}>
					{label} MFE
				</label>
				<span className={css.inputUrl}>{displayUrl}</span>
			</div>
			<input
				id={inputId}
				type="text"
				className={css.inputField}
				value={value}
				onChange={handleChange}
				placeholder="v3.6.0 | pr-2812"
				maxLength={20}
			/>
		</div>
	);
};

const VersionOverridePanel = ({ onBack, onClose }: VersionOverridePanelProps) => {
	const { mfeVersions } = useDevTools();
	const [versionsState, setVersionsState] = useState<MfeVersionsType>(mfeVersions);
	const [showReloadBanner, setShowReloadBanner] = useState(false);

	const handleVersionChange = (mfe: keyof MfeVersionsType) => {
		return (version: string) => {
			setVersionsState((prevState) => ({
				...prevState,
				[mfe]: { ...prevState[mfe], version }
			}));
		};
	};

	const handleSave = () => {
		saveDevToolsStorage(versionsState);
		setShowReloadBanner(true);
	};

	const handleReset = () => {
		const resetVersions: MfeVersionsType = {
			auth: { version: '' },
			settings: { version: '' },
			garage: { version: '' }
		};
		setVersionsState(resetVersions);
		resetDevToolsStorage();
		setShowReloadBanner(true);
	};

	const handleReload = () => {
		window.location.reload();
	};

	return (
		<div className={css.versionOverridePanel}>
			<div className={css.header}>
				<button className={css.backButton} onClick={onBack} type="button" aria-label="Back">
					<Icon name="chevronLeft" />
				</button>
				<span className={css.title}>Version Override</span>
				<button
					className={css.closeButton}
					onClick={onClose}
					type="button"
					aria-label="Close"
				>
					<Icon name="close" />
				</button>
			</div>

			<p className={css.helperText}>
				Test different MFE versions by entering version numbers or PR identifiers.{' '}
				<a
					href="https://rvlife.atlassian.net/wiki/x/KICTOQ"
					target="_blank"
					rel="noopener noreferrer"
					className={css.helperLink}
				>
					Learn more
				</a>
			</p>

			<div className={css.content}>
				<VersionInputRow
					label="Auth"
					baseUrl={getOriginFromUrl(MFE_ENVIRONMENTS.auth)}
					value={versionsState.auth.version}
					onChange={handleVersionChange('auth')}
				/>
				<VersionInputRow
					label="Settings"
					baseUrl={getOriginFromUrl(MFE_ENVIRONMENTS.settings)}
					value={versionsState.settings.version}
					onChange={handleVersionChange('settings')}
				/>
				<VersionInputRow
					label="Garage"
					baseUrl={getOriginFromUrl(MFE_ENVIRONMENTS.garage)}
					value={versionsState.garage.version}
					onChange={handleVersionChange('garage')}
				/>
			</div>

			{showReloadBanner && (
				<div className={css.reloadBanner}>
					<span>Reload to apply changes</span>
					<button className={css.reloadButton} onClick={handleReload} type="button">
						Reload
					</button>
				</div>
			)}

			<div className={css.actions}>
				<button className={css.actionButton} onClick={handleReset} type="button">
					Reset
				</button>
				<button
					className={`${css.actionButton} ${css.primary}`}
					onClick={handleSave}
					type="button"
				>
					Save
				</button>
			</div>

			<div className={css.divider} />

			<div className={css.links}>
				<a
					href="https://rvlife.atlassian.net/projects/PRO?selectedItem=com.atlassian.jira.jira-projects-plugin%3Arelease-page&status=released"
					target="_blank"
					rel="noopener noreferrer"
					className={css.link}
				>
					<span>Previous releases</span>
					<Icon name="linkNewTab" className={css.linkIcon} />
				</a>
				<a
					href="https://github.com/rv-life/rvlife-web/pulls?q=sort%3Aupdated-desc+is%3Apr+is%3Aopen"
					target="_blank"
					rel="noopener noreferrer"
					className={css.link}
				>
					<span>Open PRs</span>
					<Icon name="linkNewTab" className={css.linkIcon} />
				</a>
			</div>
		</div>
	);
};

export default VersionOverridePanel;
